/**
 * AIAgentMessageListDesktop - Pure Display Component
 * 
 * ⚠️ CRITICAL: This is a PURE DISPLAY COMPONENT - DO NOT add state management
 * 
 * ARCHITECTURE RULES:
 * ✅ ONLY displays messages from props (via ChatManager)
 * ✅ ONLY handles UI interactions (scrolling, animations)
 * ❌ NEVER loads messages directly from database
 * ❌ NEVER creates React state for chat data
 * ❌ NEVER calls MessageCrudService directly
 * 
 * See: docs/CHAT_SYSTEM_ARCHITECTURE.md for complete guidelines
 * See: docs/CHAT_SYSTEM_DEVELOPMENT_PATTERNS.md for extension patterns
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import MessageBubble from './MessageBubble';
import { BotTypingIndicator } from './BotTypingIndicator';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useChatScroll } from '@/hooks/ai-agents/useChatScroll';
import useAnimationVariants from '@/hooks/ui/use-animation-variants';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { Loader2 } from 'lucide-react';

interface AIAgentMessageListDesktopProps {
  messages: MessageDisplay[];
  isLoading?: boolean;
  threadId?: string;
  onLoadOlderMessages?: () => Promise<void>;
  hasOlderMessages?: boolean;
  isLoadingOlder?: boolean;
}

/**
 * AIAgentMessageListDesktop - Pure display component
 * 
 * ✅ SIMPLIFIED: Now only displays messages, no database loading
 * ✅ SINGLE SOURCE: Uses only fallbackMessages from ChatManager
 * ✅ CLEAN: Removed all duplicate loading logic
 */
export const AIAgentMessageListDesktop: React.FC<AIAgentMessageListDesktopProps> = ({
  messages,
  isLoading = false,
  threadId,
  onLoadOlderMessages,
  hasOlderMessages = false,
  isLoadingOlder = false
}) => {
  const { currentAssistant } = useAIAgentChat();
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<{ captureScrollPosition: () => void } | null>(null);

  // Get animation variants
  const { fade } = useAnimationVariants();

  // Track if this is the first load to handle initial positioning
  const hasLoadedMessages = useRef(false);

  // ✅ SIMPLIFIED: Use messages directly from props (ChatManager)
  const displayMessages = messages;

  // Scroll to bottom immediately when messages first load
  useEffect(() => {
    if (displayMessages.length > 0 && bottomRef.current && !hasLoadedMessages.current) {
      // Immediate scroll to bottom on initial load
      bottomRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
      hasLoadedMessages.current = true;
    }
  }, [displayMessages.length > 0]); 

  // ✅ SIMPLIFIED: Optional infinite scroll with unified scroll management
  const loadOlderMessages = useCallback(async () => {
    if (!onLoadOlderMessages || isLoadingOlder || !hasOlderMessages) return;
    
    try {
      // 🔧 CAPTURE: Use unified scroll system to capture position
      if (chatScrollRef.current?.captureScrollPosition) {
        chatScrollRef.current.captureScrollPosition();
      }
      
      await onLoadOlderMessages();
    } catch (error) {
      console.error('Failed to load older messages:', error);
    }
  }, [onLoadOlderMessages, isLoadingOlder, hasOlderMessages]);

  // ✅ SIMPLIFIED: Infinite scroll detection (only if callback provided)
  useEffect(() => {
    if (!onLoadOlderMessages) return;
    
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const handleScroll = () => {
      const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (!viewport) return;

      const { scrollTop } = viewport;
      
      // Load older messages when scrolling near the top (within 100px)
      if (scrollTop < 100 && hasOlderMessages && !isLoadingOlder) {
        loadOlderMessages();
      }
    };

    const viewport = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) {
      viewport.addEventListener('scroll', handleScroll);
      return () => viewport.removeEventListener('scroll', handleScroll);
    }
  }, [hasOlderMessages, isLoadingOlder, loadOlderMessages, onLoadOlderMessages]);

  // Auto-scroll for new messages with unified scroll position management
  // 🚀 EVENT-BASED: Auto-scroll triggered by new-agent-message events, preserves infinite scroll
  const chatScrollResult = useChatScroll(containerRef, bottomRef, displayMessages.length, isLoading, isLoadingOlder);
  chatScrollRef.current = chatScrollResult;

  // ✅ SIMPLIFIED: Deduplicate messages to prevent duplicates
  const deduplicatedMessages = React.useMemo(() => {
    return displayMessages.filter((message, index, self) => {
      if (index === 0) return true;
      const prevMessage = self[index - 1];
      return !(message.content === prevMessage.content && message.role === prevMessage.role);
    });
  }, [displayMessages]);

  const lastMessage = deduplicatedMessages.length > 0 ? deduplicatedMessages[deduplicatedMessages.length - 1] : null;
  const assistantName = lastMessage?.assistant?.name || currentAssistant?.name || 'Assistant';

  return (
    <div className="h-full w-full flex flex-col bg-white/40 dark:bg-slate-800/30 backdrop-blur-sm border border-slate-200/30 dark:border-slate-700/30 rounded-lg">
      <ScrollArea ref={scrollAreaRef} className="h-full" orientation="vertical">
        <div 
          ref={containerRef} 
          className="py-4 px-4"
        >
          {/* ✅ OPTIONAL: Loading indicator for older messages (if infinite scroll enabled) */}
          {isLoadingOlder && onLoadOlderMessages && (
            <div className="flex items-center justify-center py-2 mb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading older messages...</span>
              </div>
            </div>
          )}

          {/* ✅ CLEAN: Messages with natural spacing */}
          <div className="space-y-4">
            <AnimatePresence>
              {deduplicatedMessages.map((message, index) => {
                const messageAssistant = message.assistant || currentAssistant || null;

                return (
                  <div key={message.id}>
                    <MessageBubble 
                      message={message}
                      activeAssistant={messageAssistant}
                      isFirstInGroup={true}
                      isLastInGroup={true}
                    />
                  </div>
                );
              })}
            </AnimatePresence>

            {/* ✅ CLEAN: Typing indicator */}
            <AnimatePresence>
              {isLoading && (
                <motion.div 
                  key="typing-indicator"
                  variants={CHAT_ANIMATIONS.typingIndicator.container}
                  initial="initial" 
                  animate="animate" 
                  exit="exit"
                >
                  <BotTypingIndicator 
                    assistantName={assistantName} 
                    assistant={currentAssistant}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom reference for auto-scroll */}
          <div ref={bottomRef} className="h-1" />
        </div>
      </ScrollArea>
    </div>
  );
};

export default AIAgentMessageListDesktop; 